/**
 * SOJ Core Modern - Main JavaScript Entry Point
 *
 * This file is ready for site-specific JavaScript code.
 * All core functionality is handled by core.js and its modules.
 *
 * @package SOJ_Core_Modern
 * @since 2.0.0
 * @author Son of Jesse
 */

// Import core functionality (this will initialize all modules)
import core from "./core.js";

// Import utilities if needed for site-specific code
import { utils, events, dom, log } from "./modules/utilities.js";

// Import animation systems
import AnimationSystem from "./modules/animations.js";
import GSAPAnimationSystem from "./modules/gsap-animations.js";

// Import GSAP and plugins to expose globally
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import Slick Slider
import $ from "jquery";
import "slick-carousel";

// Import header animation functionality
//import { initHeaderScrollBehavior } from "./modules/header-animation.js";

// Import smooth scroll (Lenis)
import SmoothScroll from "./modules/smooth-scroll.js";

// Make GSAP available globally for blocks
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

// Make jQuery and Slick available globally
window.$ = $;
window.jQuery = $;

// Initialize animation systems
let animationSystem;
let gsapAnimationSystem;
let smoothScroll;

function initMegaMenu() {
  console.log("[SOJ] initMegaMenu() called");
  const menu = document.querySelector(".main-menu");

  if (!menu) {
    console.log("[SOJ] .main-menu not found - exiting initMegaMenu");
    return;
  }

  const triggers = [
    ...menu.querySelectorAll(".main-menu__link[data-menu-toggle]"),
  ];
  const panels = [...menu.querySelectorAll(".main-menu__panel")];
  const layoutContainer = menu.querySelector(".main-menu__layout");
  const rightContainer = menu.querySelector(".main-menu__right");
  const backButton = menu.querySelector(".main-menu__back-button");
  const desktopMediaQuery = window.matchMedia("(min-width: 768px)");
  let activeTrigger = null;

  console.log("[SOJ] initMegaMenu() setup complete:", {
    menu: menu,
    backButton: backButton,
    triggers: triggers.length,
    panels: panels.length,
  });

  // Test if back button can be clicked at all
  if (backButton) {
    backButton.onclick = () => {
      console.log(
        "[SOJ] ONCLICK FIRED - back button clicked via onclick property!"
      );
    };
  }

  const getPanelFromTrigger = (trigger) => {
    const panelId = trigger.getAttribute("data-menu-toggle");
    return panelId ? document.getElementById(panelId) : null;
  };

  const resetMenuCards = (panel) => {
    // Check if GSAP is available
    if (typeof gsap === "undefined" || !panel) {
      return;
    }

    // Find all menu cards in the panel and reset them
    const menuCards = panel.querySelectorAll(".menu-card");
    if (menuCards.length > 0) {
      gsap.set(menuCards, {
        opacity: 0,
        y: 20,
      });
    }
  };

  const closeActiveTrigger = () => {
    if (!activeTrigger) {
      return;
    }

    const panel = getPanelFromTrigger(activeTrigger);

    if (panel) {
      // Reset menu cards before closing
      resetMenuCards(panel);

      panel.classList.remove("is-visible");

      const panelType = panel.dataset.panelType;
      const rightContainer = panel.closest(".main-menu__right");
      const layoutContainer = panel.closest(".main-menu__layout");
      if (rightContainer && panelType) {
        rightContainer.classList.remove(`main-menu__right--${panelType}`);
      }
      if (layoutContainer && panelType) {
        layoutContainer.classList.remove(`main-menu__layout--${panelType}`);
      }
    }

    activeTrigger.setAttribute("aria-expanded", "false");
    const parentItem = activeTrigger.closest(".main-menu__item");
    if (parentItem) {
      parentItem.classList.remove("is-open");
    }

    activeTrigger = null;
    menu.classList.remove("rightside-active");
  };

  const animateMenuCards = (panel) => {
    // Check if GSAP is available
    if (typeof gsap === "undefined") {
      return;
    }

    // Find all menu cards in the panel
    const menuCards = panel.querySelectorAll(".menu-card");

    const menuCardsContainer = panel.querySelector(".rightside__column");

    if (menuCards.length === 0) {
      return;
    }

    // Set initial state - cards are invisible
    gsap.set(menuCards, {
      opacity: 0,
      //y: 20,
    });
    // Animate cards
    gsap.to(menuCards, {
      opacity: 1,
      //y: 0,
      duration: 1,
      stagger: 0.1, // Slower stagger - 80ms between each card
      ease: "power2.out",
    });

    // Animate container
    gsap.set(menuCardsContainer, {
      y: 20,
    });

    gsap.to(menuCardsContainer, {
      y: 0,
      duration: 0.6,
      ease: "power2.out",
    });
  };

  const openTrigger = (trigger) => {
    const panel = getPanelFromTrigger(trigger);

    if (!panel) {
      return;
    }

    // Ensure any server-rendered `hidden` attribute is removed so CSS can show it
    panel.removeAttribute("hidden");
    panel.classList.add("is-visible");
    trigger.setAttribute("aria-expanded", "true");

    const parentItem = trigger.closest(".main-menu__item");
    if (parentItem) {
      parentItem.classList.add("is-open");
    }

    const panelType = panel.dataset.panelType;
    const rightContainer = panel.closest(".main-menu__right");
    const layoutContainer = panel.closest(".main-menu__layout");
    if (rightContainer && panelType) {
      rightContainer.classList.add(`main-menu__right--${panelType}`);
    }
    if (layoutContainer && panelType) {
      layoutContainer.classList.add(`main-menu__layout--${panelType}`);
    }

    activeTrigger = trigger;
    menu.classList.add("rightside-active");
    console.log("[SOJ] openTrigger() - ADDING rightside-active to <html>");
    document.documentElement.classList.add("rightside-active");

    // Animate menu cards after a brief delay to ensure panel is visible
    setTimeout(() => {
      animateMenuCards(panel);
    }, 50);
  };

  const shouldAutoActivateRightSide = () => desktopMediaQuery.matches;

  const openDefaultPanel = () => {
    if (!shouldAutoActivateRightSide() || !triggers.length) {
      return;
    }

    const defaultTrigger = triggers[0];

    if (activeTrigger === defaultTrigger) {
      return;
    }

    closeActiveTrigger();
    openTrigger(defaultTrigger);
  };

  const resetMenuState = () => {
    closeActiveTrigger();

    panels.forEach((panel) => {
      panel.classList.remove("is-visible");
      panel.setAttribute("hidden", "hidden");
    });

    triggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
      const item = trigger.closest(".main-menu__item");
      if (item) {
        item.classList.remove("is-open");
      }
    });

    if (layoutContainer) {
      layoutContainer.classList.remove(
        "main-menu__layout--wide",
        "main-menu__layout--narrow"
      );
    }
    if (rightContainer) {
      rightContainer.classList.remove(
        "main-menu__right--wide",
        "main-menu__right--narrow"
      );
    }
    menu.classList.remove("rightside-active");
    document.documentElement.classList.remove("rightside-active");
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();

      if (activeTrigger === trigger) {
        closeActiveTrigger();
      } else {
        closeActiveTrigger();
        openTrigger(trigger);
      }
    });
  });

  // Handle back button click - use multiple approaches to ensure it works
  const handleBackButton = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    console.log(
      "[SOJ] Back button clicked - closing menu and removing rightside-active"
    );
    console.log(
      "[SOJ] BEFORE resetMenuState - rightside-active on html?",
      document.documentElement.classList.contains("rightside-active")
    );

    resetMenuState();

    console.log(
      "[SOJ] AFTER resetMenuState - rightside-active on html?",
      document.documentElement.classList.contains("rightside-active")
    );

    // Ensure rightside-active is removed from HTML element (resetMenuState should handle this, but double-check)
    document.documentElement.classList.remove("rightside-active");

    console.log(
      "[SOJ] AFTER manual remove - rightside-active on html?",
      document.documentElement.classList.contains("rightside-active")
    );

    // Check again after a small delay to see if something re-adds it
    setTimeout(() => {
      console.log(
        "[SOJ] 100ms later - rightside-active on html?",
        document.documentElement.classList.contains("rightside-active")
      );
    }, 100);
  };

  // Debug: Log all clicks to see if we're catching them
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      const backButton = target.closest(".main-menu__back-button");

      if (backButton) {
        console.log("[SOJ] CLICK DETECTED on back button!", {
          target: target,
          backButton: backButton,
          classList: target.classList,
          tagName: target.tagName,
        });
      }
    },
    true
  );

  // Try direct event delegation on document (capture phase to catch early)
  document.addEventListener(
    "click",
    (event) => {
      const clickedBackButton = event.target.closest(".main-menu__back-button");
      if (clickedBackButton) {
        console.log("[SOJ] Back button detected via event delegation");
        handleBackButton(event);
      }
    },
    true
  ); // Use capture phase to catch it early

  // Also try directly on menu if button exists
  if (backButton) {
    console.log("[SOJ] Back button found, adding direct listener");
    backButton.addEventListener("click", handleBackButton, true);

    // Add another listener to the SVG and span inside
    const svg = backButton.querySelector("svg");
    const span = backButton.querySelector(".main-menu__back-label");

    if (svg) {
      console.log("[SOJ] Adding listener to SVG inside back button");
      svg.addEventListener(
        "click",
        (e) => {
          console.log("[SOJ] SVG clicked directly");
          handleBackButton(e);
        },
        true
      );
    }

    if (span) {
      console.log("[SOJ] Adding listener to SPAN inside back button");
      span.addEventListener(
        "click",
        (e) => {
          console.log("[SOJ] SPAN clicked directly");
          handleBackButton(e);
        },
        true
      );
    }
  } else {
    console.warn("[SOJ] Back button not found in DOM");
  }

  document.addEventListener("mainMenuDrawerOpened", openDefaultPanel);

  const handleDesktopBreakpointChange = (event) => {
    if (event.matches) {
      openDefaultPanel();
    } else {
      resetMenuState();
    }
  };

  if (desktopMediaQuery.addEventListener) {
    desktopMediaQuery.addEventListener("change", handleDesktopBreakpointChange);
  } else if (desktopMediaQuery.addListener) {
    desktopMediaQuery.addListener(handleDesktopBreakpointChange);
  }

  // Ensure the first item is active by default on load (desktop only)
  openDefaultPanel();

  document.addEventListener("click", (event) => {
    if (!activeTrigger) {
      return;
    }

    if (
      !event.target.closest(".main-menu__left") &&
      !event.target.closest(".main-menu__right") &&
      !event.target.closest(".mobile-menu-toggle")
    ) {
      closeActiveTrigger();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && activeTrigger) {
      closeActiveTrigger();
    }
  });
}

function initMainMenuDrawer() {
  const menu = document.querySelector(".main-menu");
  const toggle = document.querySelector(".mobile-menu-toggle");
  const closeButtons = menu?.querySelectorAll(".close-button") ?? [];
  const overlay = document.querySelector(".mobile-menu-overlay");
  const scrollContainer = menu?.querySelector(".rightside__columns-wrapper");

  if (!menu || !toggle) {
    return;
  }

  const openMenu = () => {
    document.documentElement.classList.add("mobile-menu-active");
    toggle.setAttribute("aria-expanded", "true");
    menu.setAttribute("aria-hidden", "false");
    menu.dataset.menuState = "open";
    menu.classList.add("is-open");
    
    // Hide #masthead :after when main menu is active
    const masthead = document.getElementById("masthead");
    if (masthead) {
      masthead.classList.add("menu-active");
    }
    
    document.dispatchEvent(new CustomEvent("mainMenuDrawerOpened"));
  };

  const closeMenu = () => {
    document.documentElement.classList.remove("mobile-menu-active");
    toggle.setAttribute("aria-expanded", "false");
    menu.setAttribute("aria-hidden", "true");
    menu.dataset.menuState = "closed";
    menu.classList.remove("is-open");
    
    // Show #masthead :after when main menu is closed
    const masthead = document.getElementById("masthead");
    if (masthead) {
      masthead.classList.remove("menu-active");
    }
  };

  // Ensure mouse wheel scrolls the menu panel instead of the page (Lenis)
  if (scrollContainer) {
    scrollContainer.addEventListener(
      "wheel",
      (event) => {
        // Only intercept when the mobile menu is active
        if (
          !document.documentElement.classList.contains("mobile-menu-active")
        ) {
          return;
        }

        const delta = event.deltaY;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const atTop = scrollTop === 0;
        const atBottom = Math.abs(scrollHeight - clientHeight - scrollTop) <= 1;

        // If there's room to scroll in the direction of the wheel, handle it here
        if ((delta < 0 && !atTop) || (delta > 0 && !atBottom)) {
          event.preventDefault();
          event.stopPropagation();
          scrollContainer.scrollTop += delta;
        }
      },
      { passive: false }
    );
  }

  toggle.addEventListener("click", (event) => {
    event.preventDefault();

    if (document.documentElement.classList.contains("mobile-menu-active")) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  document.addEventListener("click", (event) => {
    if (!document.body.classList.contains("mobile-menu-active")) {
      return;
    }

    if (
      !event.target.closest(".main-menu") &&
      !event.target.closest(".mobile-menu-toggle")
    ) {
      closeMenu();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (
      event.key === "Escape" &&
      document.documentElement.classList.contains("mobile-menu-active")
    ) {
      closeMenu();
    }
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      closeMenu();
    });
  });

  if (overlay) {
    overlay.addEventListener("click", (event) => {
      event.preventDefault();
      closeMenu();
    });
  }
}

/**
 * Initialize learn more accordions
 * Simple slide up/down functionality
 */
function initLearnMoreAccordions() {
  const toggleLearnMore = ($heading) => {
    const $accordion = $heading.closest(".et-learn-more");
    const $content = $accordion.find(".learn-more-content");

    if ($content.length === 0) return;

    $content.slideToggle(300);

    $accordion.toggleClass("et-learn-more-open");
    const isOpen = $accordion.hasClass("et-learn-more-open");
    $heading.attr("aria-expanded", isOpen ? "true" : "false");
    $content.attr("aria-hidden", isOpen ? "false" : "true");
  };

  jQuery(".et-learn-more .heading-more").on("click", function (e) {
    e.preventDefault();
    toggleLearnMore(jQuery(this));
  });

  jQuery(".et-learn-more .heading-more").on("keydown", function (e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleLearnMore(jQuery(this));
    }
  });
}

document.addEventListener("DOMContentLoaded", function () {
  // Initialize smooth scrolling (Lenis)
  smoothScroll = new SmoothScroll();

  // MatchMedia guard: only initialize animations on desktop (>=1024px)
  const mq = window.matchMedia("(min-width: 1024px)");

  // Initialize animation systems only on desktop
  const initAnimationSystems = () => {
    if (!mq.matches) {
      console.log("[SOJ] Mobile/tablet detected - skipping animation systems");
      return;
    }

    // Initialize scroll-triggered animations (CSS-based)
    animationSystem = new AnimationSystem();

    // Wait for slider to initialize before setting up GSAP animations
    // This ensures animations are calculated AFTER pinning is set up
    const initGSAPAnimations = () => {
      // Ensure ScrollTrigger proxy is set up BEFORE creating any ScrollTrigger instances
      // This is critical for Lenis smooth scroll to work correctly with ScrollTrigger
      if (
        smoothScroll &&
        typeof smoothScroll.setupScrollTriggerProxy === "function"
      ) {
        smoothScroll.setupScrollTriggerProxy();
      }

      // Initialize GSAP animations
      gsapAnimationSystem = new GSAPAnimationSystem();

      // Make animation systems available globally for dynamic content
      window.SOJ_ANIMATIONS = animationSystem;
      window.SOJ_GSAP_ANIMATIONS = gsapAnimationSystem;
      window.SOJ_SMOOTH_SCROLL = smoothScroll;

      console.log("[SOJ] Animation systems loaded (CSS + GSAP + Lenis)");
    };

    // Listen for slider initialization event
    let sliderInitialized = false;
    document.addEventListener("sliderInitialized", () => {
      sliderInitialized = true;
      console.log("[SOJ] Slider initialized, setting up GSAP animations");
      setTimeout(initGSAPAnimations, 300);
    });

    // If no slider on page, initialize GSAP after a short delay
    setTimeout(() => {
      if (!sliderInitialized) {
        console.log("[SOJ] No slider detected, initializing GSAP animations");
        initGSAPAnimations();
      }
    }, 500);
  };

  // Teardown animation systems when viewport becomes mobile
  const teardownAnimationSystems = () => {
    // Kill GSAP animations
    if (gsapAnimationSystem && typeof gsapAnimationSystem.killAll === "function") {
      gsapAnimationSystem.killAll();
      gsapAnimationSystem = null;
    }

    // Kill all GSAP tweens and ScrollTriggers
    if (typeof gsap !== "undefined") {
      gsap.killTweensOf("*");
    }
    if (typeof ScrollTrigger !== "undefined" && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    }

    // Disconnect IntersectionObserver
    if (animationSystem && typeof animationSystem.disconnect === "function") {
      animationSystem.disconnect();
      animationSystem = null;
    }

    // Clear global references
    window.SOJ_ANIMATIONS = null;
    window.SOJ_GSAP_ANIMATIONS = null;

    console.log("[SOJ] Animation systems teardown complete");
  };

  // Handle viewport changes
  const handleViewportChange = (e) => {
    if (e.matches) {
      // Desktop: initialize animations
      initAnimationSystems();
    } else {
      // Mobile: teardown animations
      teardownAnimationSystems();
    }
  };

  // Initialize or teardown based on current viewport
  handleViewportChange(mq);

  // Listen for viewport changes
  if (mq.addEventListener) {
    mq.addEventListener("change", handleViewportChange);
  } else if (mq.addListener) {
    // Fallback for older browsers
    mq.addListener(handleViewportChange);
  }

  // Initialize header scroll behavior
  //initHeaderScrollBehavior();
  initMegaMenu();
  initMainMenuDrawer();
  initLearnMoreAccordions();
});
